import {
  ExtractionFieldValue,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
} from "components/Document/ExtractionReview";
import {
  ApplyExtractedBillOfLadingInput,
  BillOfLadingUnit,
  ExtractedBillOfLadingQuery,
  ExtractionStatus,
  ShippingDocumentLineInput,
  WasteKind,
} from "generated-graphql/graphql";
import { toFlexibleNumber } from "util/flexibleNumber";

import { MISSING_SHIP_DATE_WARNING } from "./shippingDocumentDisplay";

/** The repeated group key, which is also the extractor's own array field path. */
const LINES = "lines";

const BILL_UNIT_LABELS: Record<BillOfLadingUnit, string> = {
  [BillOfLadingUnit.Barrels]: "Barrels",
  [BillOfLadingUnit.CubicFeet]: "Cubic feet",
  [BillOfLadingUnit.CubicYards]: "Cubic yards",
  [BillOfLadingUnit.Gallons]: "Gallons",
  [BillOfLadingUnit.Kilograms]: "Kilograms",
  [BillOfLadingUnit.Liters]: "Liters",
  [BillOfLadingUnit.MetricTons]: "Metric tons",
  [BillOfLadingUnit.Pounds]: "Pounds",
  [BillOfLadingUnit.Tons]: "Tons",
};

const UNIT_OPTIONS = Object.values(BillOfLadingUnit).map((value) => ({
  value,
  label: BILL_UNIT_LABELS[value],
}));

/**
 * Distinct from `WasteStream.wasteKind`, which process-manifests computes —
 * this is what the document itself declares. "Blank" (an ordinary line the
 * document says nothing special about) is the grid's own "—" option, not one
 * of these.
 */
export const DECLARED_WASTE_KIND_LABELS: Record<WasteKind, string> = {
  [WasteKind.Rcra]: "RCRA",
  [WasteKind.UniversalWaste]: "Universal Waste",
  [WasteKind.UsedOil]: "Used Oil",
  [WasteKind.Pharmaceutical]: "Pharmaceutical",
};

const DECLARED_WASTE_KIND_OPTIONS = Object.values(WasteKind).map((value) => ({
  value,
  label: DECLARED_WASTE_KIND_LABELS[value],
}));

/**
 * The bill of lading's fields as the reviewer sees them, keyed by the
 * extractor's own field paths — `documentNumber`, `lines[0].description` —
 * so a confidence score addresses a control without any translation, the
 * same posture `WASTE_PROFILE_REVIEW_SCHEMA` (7C) takes.
 *
 * Facility is deliberately not a grid field: decision 4 puts a facility
 * picker in the dialog chrome above the grid instead, since it is confirmed
 * once per document rather than per line.
 */
export const BILL_OF_LADING_REVIEW_SCHEMA: ExtractionReviewSchema = {
  sections: [
    {
      key: "header",
      title: "Document details",
      fields: [
        { key: "documentNumber", label: "Document number", kind: "text" },
        { key: "generatorName", label: "Generator", kind: "text" },
        { key: "transporterName", label: "Transporter", kind: "text" },
        { key: "destinationName", label: "Destination", kind: "text" },
        {
          key: "shipDate",
          label: "Ship date",
          kind: "text",
          helperText: "YYYY-MM-DD",
          // Saving without one is allowed. What a reviewer cannot guess is that
          // Shipment Documents is scoped by the Year picker, which a manifest
          // with no ship date does not match.
          emptyWarning: MISSING_SHIP_DATE_WARNING,
        },
        {
          key: "receivedDate",
          label: "Received date",
          kind: "text",
          helperText: "YYYY-MM-DD",
        },
      ],
    },
  ],
  rowGroups: [
    {
      key: LINES,
      title: "Lines",
      rowNoun: "line",
      /*
       * The description alone. A line restated at a different quantity is the
       * same waste re-weighed at the scale, which is the ordinary case on this
       * paperwork and reads as a changed cell; including quantity would make it
       * a line the document does not have and ask the reviewer about an
       * addition instead of a change.
       *
       * Carrier paperwork has no standard layout and can repeat a description
       * across lines — two drums of the same waste — so this is not a unique
       * key. `pairRecordRows` holds one queue of extracted rows per identity
       * and takes from the front, so two identical descriptions pair with two
       * separate lines rather than both landing on the first. Where the counts
       * differ, the surplus is reported as rows to keep or remove, which is
       * the question the reviewer should be asked.
       */
      identityColumns: ["description"],
      columns: [
        { key: "description", label: "Description", kind: "text" },
        { key: "quantity", label: "Quantity", kind: "number" },
        { key: "unit", label: "Unit", kind: "select", options: UNIT_OPTIONS },
        {
          key: "declaredWasteKind",
          label: "Declared waste kind",
          kind: "select",
          options: DECLARED_WASTE_KIND_OPTIONS,
        },
        {
          key: "federalWasteCodes",
          label: "Federal waste codes",
          kind: "text",
          helperText: "Comma-separated",
        },
        {
          key: "stateWasteCodes",
          label: "State waste codes",
          kind: "text",
          helperText: "Comma-separated",
        },
      ],
    },
  ],
};

type Reading = NonNullable<ExtractedBillOfLadingQuery["extractedBillOfLading"]>;

/**
 * The distinct kinds the reading's lines declare, ordered by the enum rather
 * than by where the lines fall, so one document always presents the same set
 * however its lines happen to be sequenced.
 *
 * What the document declares, not what it means for compliance. Whether a kind
 * counts toward generator status is the engine's answer and varies by state and
 * by kind, so stating it here would restate a rule this surface does not own.
 *
 * Reflects the extraction, not the reviewer's live edits: the form state lives
 * inside `ExtractionReviewGrid`, and reaching into it would mean widening that
 * shared component for one caller. The surrounding copy says the reading is what
 * the extractor found, which keeps that honest.
 */
export function declaredKinds(reading: Pick<Reading, "lines"> | null | undefined): WasteKind[] {
  const declared = new Set(
    (reading?.lines ?? [])
      .map((line) => line.declaredWasteKind)
      .filter((kind): kind is WasteKind => kind != null)
  );
  return Object.values(WasteKind).filter((kind) => declared.has(kind));
}

/**
 * The status and error the review grid should show.
 *
 * A reading carrying an error is not reviewable, and the document's own
 * extraction status does not say so: the enrichment job that wrote the payload
 * succeeded, so the document reads as SUCCEEDED while the candidate holds
 * either the extractor's failure or the resolver's "cannot be read back"
 * message for a payload written under a schema it can no longer parse. Showing
 * the document's status alone leaves the reviewer an empty grid and no reason
 * for it.
 */
export function toGridExtractionState(
  reading: Pick<Reading, "error"> | null | undefined,
  document:
    | { extractionStatus?: ExtractionStatus | null; extractionError?: string | null }
    | null
    | undefined
): { status: ExtractionStatus | null | undefined; extractionError: string | null | undefined } {
  if (reading?.error != null) {
    return { status: ExtractionStatus.Failed, extractionError: reading.error };
  }
  return {
    status: document?.extractionStatus,
    extractionError: document?.extractionError,
  };
}

/** Join codes for the grid's single comma-separated text field. */
function joinCodes(codes: readonly string[] | null | undefined): string | null {
  if (!codes || codes.length === 0) return null;
  return codes.join(", ");
}

/**
 * The reading in the shape `ExtractionReviewGrid` consumes.
 *
 * The API already keys `fieldConfidences` by field path in the same
 * `lines[0].description` form `rowConfidenceKey` builds, so confidence
 * passes through unmapped — the same posture 7C's `toExtractionResult`
 * documents for its own reading.
 */
function toValues(values: Reading["values"]): Record<string, ExtractionFieldValue> {
  return {
    documentNumber: values.documentNumber ?? null,
    generatorName: values.generatorName ?? null,
    transporterName: values.transporterName ?? null,
    destinationName: values.destinationName ?? null,
    // `shipDate` is a `Date` scalar on the wire but a free-typed field here —
    // see `ApplyExtractedBillOfLadingInput`'s doc comment on why the reviewer
    // edits it as text rather than through a date picker.
    shipDate: values.shipDate == null ? null : String(values.shipDate),
    receivedDate: values.receivedDate == null ? null : String(values.receivedDate),
  };
}

function toRows(lines: Reading["lines"]): Record<string, Record<string, ExtractionFieldValue>[]> {
  return {
    [LINES]: lines.map((line) => ({
      description: line.description ?? null,
      quantity: line.quantity ?? null,
      unit: line.unit ?? null,
      declaredWasteKind: line.declaredWasteKind ?? null,
      federalWasteCodes: joinCodes(line.federalWasteCodes),
      stateWasteCodes: joinCodes(line.stateWasteCodes),
    })),
  };
}

export function toExtractionResult(reading: Reading): ExtractionResult {
  return {
    // The reviewer's own saved corrections over the model's reading: the
    // resolver runs `applyCandidateCorrections` before returning `values`.
    values: toValues(reading.values),
    rows: toRows(reading.lines),
    // What the model read, uncorrected. The grid shows this as "Suggested: x"
    // beside a field the reviewer has since changed, and offers it back on
    // click. Omitting it makes `extractedBaseline` fall back to `values`, so a
    // second review pass presents a reviewer's own earlier edit as the
    // extraction and the model's reading is gone from the screen.
    extractedValues: toValues(reading.asExtractedValues),
    extractedRows: toRows(reading.asExtractedLines),
    confidence: reading.fieldConfidences
      .filter((entry) => entry.confidence != null)
      .map((entry) => ({ field: entry.fieldPath, score: entry.confidence ?? 0 })),
  };
}

const UNIT_VALUES: readonly string[] = Object.values(BillOfLadingUnit);
const WASTE_KIND_VALUES: readonly string[] = Object.values(WasteKind);

const isBillUnit = (value: string): value is BillOfLadingUnit => UNIT_VALUES.includes(value);
const isWasteKind = (value: string): value is WasteKind => WASTE_KIND_VALUES.includes(value);

const toUnit = (value: ExtractionFieldValue | undefined): BillOfLadingUnit | null =>
  typeof value === "string" && isBillUnit(value) ? value : null;

const toWasteKind = (value: ExtractionFieldValue | undefined): WasteKind | null =>
  typeof value === "string" && isWasteKind(value) ? value : null;

function toText(value: ExtractionFieldValue | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * A number passes through. So does a string the whole of which is a number,
 * including one written with thousands separators. Anything else becomes null
 * rather than reaching the mutation as text the `Float` input would reject by
 * type. The reviewer sees the field empty on the next read, which is the cost of
 * keeping the apply itself from failing on a typo.
 */
function toQuantity(value: ExtractionFieldValue | undefined): number | null {
  const flexible = toFlexibleNumber(value);
  return typeof flexible === "number" ? flexible : null;
}

/**
 * Split a comma-separated codes field back into a clean array.
 *
 * A blank cell is an empty array, never null. The extraction payload types
 * both code fields as non-null arrays and the prompt asks for `[]` when the
 * document shows no codes, so the reviewer has no way to mean "unknown" here —
 * blank means "none printed". Sending null instead would read as a correction
 * away from `[]` on every line that simply has no codes, marking an untouched
 * field as a human edit.
 */
function toCodes(value: ExtractionFieldValue | undefined): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code !== "");
}

function toLine(row: Record<string, ExtractionFieldValue>): ShippingDocumentLineInput {
  return {
    description: toText(row.description),
    quantity: toQuantity(row.quantity),
    unit: toUnit(row.unit),
    declaredWasteKind: toWasteKind(row.declaredWasteKind),
    federalWasteCodes: toCodes(row.federalWasteCodes),
    stateWasteCodes: toCodes(row.stateWasteCodes),
  };
}

/**
 * The reviewer's confirmed values as an apply input.
 *
 * `shipDate` is passed through as whatever text the reviewer left — trimmed,
 * or null when blank — rather than parsed here. The `Date` scalar's own
 * `parseValue` (`new Date(value)`) is lenient about format, and the field's
 * helper text already steers toward `YYYY-MM-DD`; a string it truly can't
 * read surfaces as a save error naming the field, matching this grid's
 * "server is the validation gate" posture rather than silently nulling a
 * value someone typed.
 */
export function toApplyInput(
  reviewed: ExtractionReviewValues,
  { documentId, facilityId }: { documentId: string; facilityId: string }
): ApplyExtractedBillOfLadingInput {
  const { values, rows } = reviewed;
  const lines = rows[LINES] ?? [];
  return {
    documentId,
    // Sent so the apply records each line's corrections against the extracted
    // line it came from. Without it the server falls back to position, and a
    // line the reviewer deleted or inserted shifts every line below it.
    lineExtractedIndexes: lines.map((line) => line.extractedIndex),
    document: {
      facilityId,
      documentNumber: toText(values.documentNumber),
      generatorName: toText(values.generatorName),
      transporterName: toText(values.transporterName),
      destinationName: toText(values.destinationName),
      shipDate: toText(values.shipDate),
      receivedDate: toText(values.receivedDate),
      lines: lines.map((line) => toLine(line.values)),
    },
  };
}
